import type { FlatRubricItem, RubricCategory } from '../types';

/* ============================================================
   RUBRIC — the durable asset. Each criterion carries its tier,
   what to assess, and the figures it draws evidence from.
   ============================================================ */
export const RUBRIC: RubricCategory[] = [
  {
    cat: 'A',
    name: 'Channel First Impression',
    tag: 'Landing surface',
    items: [
      {
        id: 'A1',
        title: 'Value-proposition legibility',
        tier: 'T1',
        figs: ['banner', 'about', 'homepage'],
        assess:
          'Can a stranger say what this channel is for, and who it is for, within ~3 seconds of the banner + name + first lines of About?',
      },
      {
        id: 'A2',
        title: 'Banner quality & mobile safe-area',
        tier: 'T1',
        figs: ['banner', 'banner_mobile'],
        assess: 'Is key banner text inside the mobile safe area, readable, current, not pixelated?',
      },
      {
        id: 'A3',
        title: 'About-page completeness',
        tier: 'T1',
        figs: ['about'],
        assess: 'Clear elevator pitch, working links, stated upload schedule, a call to action?',
      },
      {
        id: 'A4',
        title: 'Channel layout & featured content',
        tier: 'T1',
        figs: ['homepage'],
        assess:
          'Is the homepage organised — featured video for non-subscribers, guiding playlists, logical sections — or a raw wall of uploads?',
      },
    ],
  },
  {
    cat: 'B',
    name: 'Channel Trailer',
    tag: 'Campaign gate',
    items: [
      {
        id: 'B1',
        title: 'Trailer presence',
        tier: 'T1',
        figs: ['homepage'],
        assess:
          'Is there a channel trailer for non-subscribed visitors? Hard yes/no. For a SUBSCRIBER campaign this is a gate, not a nice-to-have.',
      },
      {
        id: 'B2',
        title: 'Trailer quality (if present)',
        tier: 'T1',
        figs: ['homepage'],
        assess:
          'If a trailer exists, score it against best practices from its metadata + transcript: (1) hook in first ~5 seconds, (2) states the problem it solves, (3) shows the solution / what the channel offers, (4) explains what the channel is about and who it is for, (5) explicit subscribe CTA, (6) length ~30–90s. Name which are present/missing. If no transcript, judge on title/description/duration and flag the gap.',
      },
    ],
  },
  {
    cat: 'C',
    name: 'Thumbnails & Click Appeal',
    tag: 'Click surface',
    items: [
      {
        id: 'C1',
        title: 'Grid consistency',
        tier: 'T1',
        figs: ['grid'],
        assess:
          'Across the top ~12 thumbnails: coherent style and branding, or chaotic mix? Consistency signals professionalism to a browsing visitor.',
      },
      {
        id: 'C2',
        title: 'Thumbnail clickability',
        tier: 'T1→T2',
        figs: ['grid', 'video'],
        assess:
          'Contrast, single focal point, legible at small size, not overcrowded. The common failure trio: low contrast, too much text, no focal point. (T2: confirm against CTR — below ~3% browse is a problem; 5–8% is the target.)',
      },
      {
        id: 'C3',
        title: 'Winner-vs-loser contrast',
        tier: 'T1→T2',
        figs: ['video', 'grid'],
        assess:
          'Compare the most-viewed thumbnails against conspicuously low-view recent ones. What do the winners share that the losers lack? Name the specific pattern.',
      },
    ],
  },
  {
    cat: 'D',
    name: 'Titles & Metadata',
    tag: 'Discovery',
    items: [
      {
        id: 'D1',
        title: 'Title clarity & front-loading',
        tier: 'T1',
        figs: ['video'],
        assess:
          'Is the keyword/benefit in the first ~40 characters (not cut off)? Curiosity without clickbait? Sweet spot ~20–60 chars.',
      },
      {
        id: 'D2',
        title: 'Search-intent presence',
        tier: 'T1→T2',
        figs: ['video'],
        assess:
          'Do titles target things people actually search, or vague internal phrasing ("My Vacation" vs "How to Plan X")? This is the single highest-frequency failure across audited channels.',
      },
      {
        id: 'D3',
        title: 'Description hook & structure',
        tier: 'T1',
        figs: ['video'],
        assess:
          'Do the first 1–2 lines hook before the "show more" fold? Are there links, timestamps, a CTA?',
      },
    ],
  },
  {
    cat: 'E',
    name: 'Content Strategy & Cadence',
    tag: 'Ongoing',
    items: [
      {
        id: 'E1',
        title: 'Publishing consistency',
        tier: 'T1',
        figs: [],
        assess:
          'From the last ~10 upload dates: roughly weekly over the past 3 months is healthy; gaps or dormancy is a risk. Do not drive paid traffic to a channel that looks abandoned.',
      },
      {
        id: 'E2',
        title: 'Content pillar / recognizability',
        tier: 'T1',
        figs: ['grid'],
        assess:
          'Is there a visible topic the channel is becoming known for, or is it scattered? (Strategic — likely beyond a one-time prep package.)',
      },
      {
        id: 'E3',
        title: 'Shorts presence & separation',
        tier: 'T1',
        figs: ['homepage'],
        assess:
          'Does the channel have Shorts, on a separate shelf? For subscriber campaigns Shorts are a distinct acquisition lever.',
      },
    ],
  },
  {
    cat: 'F',
    name: 'Retention & Hook',
    tag: 'Performance',
    items: [
      {
        id: 'F1',
        title: 'First-30-seconds hook',
        tier: 'T2',
        figs: ['video'],
        assess:
          'T2: from the retention graph, is there a sharp early drop (weak hook)? T1: only a weak signal from watching the intro of sample videos — flag as inference.',
      },
    ],
  },
  {
    cat: 'G',
    name: 'Community & Trust',
    tag: 'Stickiness',
    items: [
      {
        id: 'G1',
        title: 'Engagement signals',
        tier: 'T1',
        figs: ['homepage', 'video'],
        assess:
          'Comments on/off, creator replies, pinned comment, active Community tab? Two-way engagement improves subscriber stickiness.',
      },
    ],
  },
  {
    cat: 'H',
    name: 'Strategy & Objectives',
    tag: 'The bigger picture',
    items: [
      {
        id: 'H1',
        title: 'Objective consistency',
        tier: 'T1',
        figs: ['about', 'banner', 'homepage'],
        assess:
          "Given the channel's stated objectives (what it wants a viewer to DO, in priority order — e.g. 1. subscribe, 2. download the app, 3. join community), does every surface lead to them PROPORTIONALLY to their priority? Count how consistently each objective appears across About, video descriptions, trailer and banner. Flag any objective that is stated as important but under-served — e.g. \"the app is objective #2 but appears in only a fraction of descriptions, is buried in the About, and is absent from the trailer; it needs systematic emphasis across every surface.\" Judge by presence/absence and prominence, not vibes.",
      },
      {
        id: 'H2',
        title: 'Series & style strategy',
        tier: 'T1',
        figs: ['homepage', 'video'],
        assess:
          'Does the channel run in SERIES — batches of videos sharing one visual/title template — and switch template between series? This is a deliberate approach, not chaos: recognise it. Verdict depends on goal. For channel browsing, in-series consistency is GOOD (viewers recognise a series and binge it). For a paid campaign where a video becomes the ad creative, style variance BETWEEN series weakens a single recognisable channel identity. Do not say "thumbnails are inconsistent" — instead: "the channel runs N series, each internally consistent; for the campaign, pick one series as the creative face." Name the series you can see and which one is the strongest candidate.',
      },
    ],
  },
];

/** Every rubric item flattened with its parent category name + letter. */
export const ALL_ITEMS: FlatRubricItem[] = RUBRIC.flatMap((c) =>
  c.items.map((i) => ({ ...i, cat: c.name, catLetter: c.cat })),
);
